// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import type { AboutYouSectionContent, ProposalSectionClientFact } from '../../../types/proposal';

interface Props {
  readonly content: AboutYouSectionContent;
}

const FACT_ROWS: Array<{ key: keyof ProposalSectionClientFact; label: string }> = [
  { key: 'dateOfBirth', label: 'Date of birth' },
  { key: 'currentAge', label: 'Current age' },
  { key: 'maritalStatus', label: 'Marital status' },
  { key: 'taxResidency', label: 'Tax residency' },
  { key: 'taxDomicile', label: 'Tax domicile' },
  { key: 'attitudeToRisk', label: 'Attitude to risk' },
];

/** ABOUT_YOU — per-client facts card(s) + dependants table (read-only). */
const PlanAboutYouSection: React.FC<Props> = ({ content }) => {
  const clients = content.snapshot?.clients ?? [];
  const dependants = content.snapshot?.dependants ?? [];

  return (
    <div data-testid="plan-about-you">
      <div className="proposal-plan__facts-grid">
        {clients.map((client) => (
          <div className="proposal-plan__facts-card" key={client.name} data-testid="plan-client-facts">
            <div className="proposal-plan__card-title">{client.name}</div>
            <dl className="proposal-plan__facts-list">
              {FACT_ROWS.filter(({ key }) => client[key] != null).map(({ key, label }) => (
                <div className="proposal-plan__facts-row" key={key}>
                  <dt className="proposal-plan__facts-label">{label}</dt>
                  <dd className="proposal-plan__facts-value">{String(client[key])}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>

      {dependants.length > 0 && (
        <div className="proposal-plan__table-wrap">
          <table className="proposal-plan__table" data-testid="plan-dependants-table">
            <thead>
              <tr>
                <th>Dependant</th>
                <th>Relationship</th>
                <th>Age</th>
              </tr>
            </thead>
            <tbody>
              {dependants.map((d, i) => (
                <tr key={`${d.name}-${i}`}>
                  <td>{d.name}</td>
                  <td>{d.relationship}</td>
                  <td>{d.age ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PlanAboutYouSection;
